import { FC, useState, useMemo, useEffect, RefObject, JSX } from "react";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESSelect, QESButton } from "@app/shared/qes-standards";
import { ListBox } from "@app/shared";
import { BasicOperatorType, LogicalOperatorType } from "@app/exports/enumerations";
import { xorWith, isEqual, isEmpty, cloneDeep } from "lodash"
import { ColumnConfig, Waffles, wafflesKeyColumnName, TextAlignment } from "@app/shared/waffles";


interface FilterCriteriaProps {
  fieldNames: string[];
  filterInvalidRecords?: boolean;
  disabled?: boolean;
  defaultCriteria?: FilterCriteriaModel[];
  onChange: (response: FilterCriteriaOnChangeResponse) => void;
  onSave?: (response: FilterCriteriaModel[]) => void;
  saveButtonRef?: RefObject<HTMLButtonElement>;
  showFilterInvalidRecordsOption?: boolean;
}

interface FilterCriteriaOnChangeResponse {
  criteria: FilterCriteriaModel[];
  filterInvalidRecords: boolean;
  hasChanges: boolean;
  hasErrors: boolean;
}

interface AddFilterModel {
  Criteria: FilterCriteriaModel[];
  FilterInvalidRecords: boolean;
}

interface FilterCriteriaModel {
  field: string;
  values: string[];
  opCode: string;
  needsQuotes: boolean;
  groupWithPrevious: boolean;
  nextConnector: string;
  needsBrackets: boolean;
  isUnaryOpCode: boolean;
  isSetOpCode: boolean;
  readText: string;
}

const criteriaColumnConfig: ColumnConfig = {
  andOr: {
    alignment: TextAlignment.Center,
    displayText: "And/Or",
    noResize: true,
    noSort: true,
    testId: "andOr"
  },
  buttons: {
    alignment: TextAlignment.Center,
    displayText: "",
    noResize: true,
    noSort: true,
    sizeContentToFit: true,
    testId: "buttonColumn"
  },
  field: {
    alignment: TextAlignment.Center,
    displayElement: <>Field<span className="text-danger"> *</span></>,
    noResize: true,
    noSort: true,
    testId: "field"
  },
  groupWithPrevious: {
    alignment: TextAlignment.Center,
    displayText: "Group With Previous",
    noResize: true,
    noSort: true,
    testId: "groupWithPrevious"
  },
  operator: {
    alignment: TextAlignment.Center,
    displayElement: <>Operator<span className="text-danger"> *</span></>,
    noResize: true,
    noSort: true,
    testId: "operator"
  },
  values: {
    alignment: TextAlignment.Center,
    displayElement: <>Values<span className="text-danger"> *</span></>,
    minWidth: "180px",
    noResize: true,
    noSort: true,
    testId: "values"
  }
}

interface CriteriaTableData {
  [wafflesKeyColumnName]: string;
  buttons: JSX.Element;
  groupWithPrevious: JSX.Element;
  field: JSX.Element;
  operator: JSX.Element;
  values: JSX.Element;
  andOr: JSX.Element;
}

const FilterCriteria: FC<React.PropsWithChildren<FilterCriteriaProps>> = (props: FilterCriteriaProps) => {
  const [criteria, setCriteria] = useState<FilterCriteriaModel[]>();
  const [originalCriteria, setOriginalCriteria] = useState<FilterCriteriaModel[]>()
  const [originalFilterInvalidRecords, setOriginalFilterInvalidRecords] = useState(false);
  const [fieldNamesDisplay, setFieldNamesDisplay] = useState<JSX.Element[]>();
  const [filterInvalidRecords, setFilterInvalidRecords] = useState(false);
  const [basicOperatorsDisplay, setBasicOperatorsDisplay] = useState<JSX.Element[]>();
  const [logicalOperatorsDisplay, setLogicalOperatorsDisplay] = useState<JSX.Element[]>();
  const [hasChanges, setHasChanges] = useState(false);
  const [nonValueOperators] = useState([BasicOperatorType.IsEmpty, BasicOperatorType.IsNotEmpty, BasicOperatorType.IsNull, BasicOperatorType.IsNotNull]);
  const [isValid, setIsValid] = useState(true);

  useEffect(() => {
    if (!!props.fieldNames) {
      setFieldNamesDisplay(
        props.fieldNames
          .sort((a, b) => { return a.localeCompare(b, undefined, { sensitivity: "base" }); })
          .map(x => (<option key={x} value={x}>{x}</option>)));
    }

    let selectOptions: JSX.Element[] = []
    for (const key in BasicOperatorType) {
      if (key === BasicOperatorType.Unknown)
        continue;

      selectOptions.push(<option key={key} value={key}>{BasicOperatorType[key as keyof typeof BasicOperatorType]}</option>)
    }
    setBasicOperatorsDisplay(selectOptions);

    selectOptions = [];
    for (const key in LogicalOperatorType) {
      if (key === LogicalOperatorType.Unknown)
        continue;

      selectOptions.push(<option key={key} value={key}>{LogicalOperatorType[key as keyof typeof LogicalOperatorType]}</option>)
    }
    setLogicalOperatorsDisplay(selectOptions);
  }, []);

  useEffect(() => {
    if (!!props.fieldNames) {
      setFieldNamesDisplay(
        props.fieldNames
          .sort((a, b) => { return a.localeCompare(b, undefined, { sensitivity: "base" }); })
          .map(x => (<option key={x} value={x}>{x}</option>)));
    }
  }, [props.fieldNames]);

  useEffect(() => {
    const criteria = props.defaultCriteria ?? [];
    setCriteria(cloneDeep(criteria));
    setOriginalCriteria(cloneDeep(criteria));
  }, [props.defaultCriteria]);

  useEffect(() => {
    const filterInvalidRecords = props.filterInvalidRecords ?? false;
    setFilterInvalidRecords(filterInvalidRecords);
    setOriginalFilterInvalidRecords(filterInvalidRecords);
  }, [props.filterInvalidRecords]);

  useEffect(() => {
    let tempValid = true;

    if (!criteria && filterInvalidRecords == originalFilterInvalidRecords)
      return;

    if (criteria) {
      for (let i = 0; i < criteria.length; i++) {
        if (!criteria[i].field || !validateFieldExists(criteria[i].field) || !criteria[i].opCode || !handleIsValuesValid(criteria[i].opCode, criteria[i].values) || !handleIsNextConnectorValid(criteria[i].nextConnector, i)) {
          tempValid = false;
          break;
        }
      }
    }

    const response: FilterCriteriaOnChangeResponse = {
      criteria: criteria,
      filterInvalidRecords: filterInvalidRecords,
      hasChanges: !isEmpty(xorWith(criteria, originalCriteria, isEqual)) || (filterInvalidRecords != originalFilterInvalidRecords),
      hasErrors: !tempValid
    }
    setHasChanges(response.hasChanges);
    setIsValid(tempValid);

    props.onChange(response);
  }, [criteria, props.fieldNames, filterInvalidRecords]);

  const handleIsValuesValid = (opCode: string, values: string[]): boolean => {
    return values.length > 0 || nonValueOperators.indexOf(BasicOperatorType[opCode as keyof typeof BasicOperatorType]) > -1;
  }

  const handleIsNextConnectorValid = (nextConnector: string, index: number): boolean => {
    //nextConnector can only be blank when it's the last criteria in the list
    return !!nextConnector || criteria.length - 1 === index;
  }

  const validateFieldExists = (fieldName: string): boolean => {
    return !fieldName || props.fieldNames.includes(fieldName)
  }

  const criteriaTableData = useMemo(() => {
    let data: CriteriaTableData[] = [];

    if (!!criteria) {
      /* eslint-disable sort-keys */
      data = criteria.map((crit, index) => {

        //Trigger field validation immediately if "Field" has been filled out, but it's not in the list of available field names
        const isFieldNotFound = !validateFieldExists(crit.field);
        let fieldErrorMsg = "";
        if (isFieldNotFound)
          fieldErrorMsg = `"${crit.field}" is not a valid field under the selected source network.`;

        const field =
          <QESSelect
            testId={`filter-criteria__field-${index}`}
            label=""
            hideLabel={true}
            placeholder="---"
            allowEmpty={true}
            disabled={!!props.disabled}
            required={true}
            isValid={!!crit.field && !isFieldNotFound}
            errorText={fieldErrorMsg}
            wasFocusedExternally={isFieldNotFound}
            value={crit.field}
            onChange={(e) => {
              setCriteria(prevCriteria => {
                prevCriteria[index].field = e.target.value;
                return [...prevCriteria];
              });
            }}
          >
            {fieldNamesDisplay}
          </QESSelect>

        return {
          key: index.toString(),
          buttons: (
            <>
              {!props.disabled ?
                (<div className="pt-1">
                  <a id={`filter-criteria__inline-add-${index}`} className="mr-2" onClick={() => handleAdd(index)}>
                    <i className="fa fa-plus text-info" />
                  </a>
                  <a id={`filter-criteria__inline-remove-${index}`} onClick={() => handleRemove(index)}>
                    <i className="fa fa-times text-danger" />
                  </a>
                </div>) : null
              }
            </>
          ),
          groupWithPrevious: (
            <QESCheckbox
              label=""
              testId={`filter-criteria__group-w-prev-${index}`}
              disabled={!!props.disabled || index === 0}
              checked={crit.groupWithPrevious}
              onChange={(e) => {
                setCriteria(prevCriteria => {
                  prevCriteria[index].groupWithPrevious = e.target.checked;
                  return [...prevCriteria];
                });
              }} />
          ),
          field: field,
          operator: (
            <QESSelect
              testId={`filter-criteria__operator-${index}`}
              label=""
              hideLabel={true}
              placeholder="---"
              allowEmpty={true}
              disabled={!!props.disabled}
              isValid={!!crit.opCode}
              value={crit.opCode}
              onChange={(e) => {
                setCriteria(prevCriteria => {
                  prevCriteria[index].opCode = e.target.value;
                  return [...prevCriteria];
                });
              }}
            >
              {basicOperatorsDisplay}
            </QESSelect>
          ),
          values: (
            <>
              {nonValueOperators.indexOf(BasicOperatorType[crit.opCode as keyof typeof BasicOperatorType]) === -1 ?
                (<ListBox
                  testId={`filter-criteria__values-${index}`}
                  disabled={!!props.disabled}
                  required={true}
                  isValid={handleIsValuesValid(crit.opCode, crit.values)}
                  value={crit.values}
                  onChange={(value) => {
                    setCriteria(prevCriteria => {
                      prevCriteria[index].values = value;
                      return [...prevCriteria];
                    });
                  }}
                />) : (<h6>No values necessary.</h6>)
              }
            </>
          ),
          andOr: (
            <QESSelect
              testId={`filter-criteria__and-or-${index}`}
              label=""
              hideLabel={true}
              disabled={!!props.disabled}
              isValid={handleIsNextConnectorValid(crit.nextConnector, index)}
              value={crit.nextConnector}
              onChange={(e) => {
                setCriteria(prevCriteria => {
                  prevCriteria[index].nextConnector = e.target.value;
                  return [...prevCriteria];
                });
              }}
            >
              {logicalOperatorsDisplay}
            </QESSelect>
          )
        }
      });
      /* eslint-enable sort-keys */
    }
    return data;
  }, [criteria, fieldNamesDisplay, basicOperatorsDisplay, logicalOperatorsDisplay, props.disabled]);

  const handleAdd = (index?: number) => {
    setCriteria(prevCriteria => {
      const newCriteria: FilterCriteriaModel = {
        field: "",
        groupWithPrevious: false,
        isSetOpCode: false,
        isUnaryOpCode: false,
        needsBrackets: false,
        needsQuotes: false,
        nextConnector: "And",
        opCode: "",
        readText: "",
        values: [],
      }

      if (index === null) {
        prevCriteria.push(newCriteria);
      } else {
        prevCriteria.splice(index, 0, newCriteria);
      }

      return [...prevCriteria];
    });
  }

  const handleRemove = (index: number) => {
    setCriteria(prevCriteria => {
      prevCriteria.splice(index, 1);
      return [...prevCriteria];
    });
  }

  const handleCancel = () => {
    setCriteria(cloneDeep(originalCriteria));
    setFilterInvalidRecords(originalFilterInvalidRecords);
  }

  return (
    <div className="card shadow-none">
      <div className="card-header border-0 gray-500-bg pt-3 pb-3 pl-3">
        <strong>Filter Criteria</strong>
      </div>
      <Waffles<CriteriaTableData>
        columns={criteriaColumnConfig}
        data={criteriaTableData}
        fullWidth={true}
        testId="criteria-waffles-table"
      />
      {!props.disabled ?
        (<div className="mt-1">
          <QESButton qesTestId="filter-criteria__add-button" qesType="secondary" onClick={() => handleAdd(null)} >
            <i className="fa fa-plus mr-1"></i>Add New Clause
          </QESButton>
          {!!props.showFilterInvalidRecordsOption ?
              <QESCheckbox
                testId={"provider-detail__validation-filter"}
                label="Filter out invalid records"
                checked={filterInvalidRecords}
                onChange={(e) => setFilterInvalidRecords(e.target.checked)}
                containerClassName="mb-3"
              />
            : null}
          {hasChanges ?
            <>
              {!!props.onSave ?
                <QESButton
                  qesTestId="filter-criteria__save-button"
                  qesType="primary"
                  className="pull-right"
                  disabled={!isValid}
                  onClick={() => props.onSave(criteria)}
                  qesRef={props.saveButtonRef}
                >
                  <i className="fa fa-floppy-o mr-1"></i>Save
                </QESButton> : null
              }
              <QESButton qesTestId="filter-criteria__cancel-button" qesType="secondary-danger" className="pull-right" onClick={() => handleCancel()} >
                <i className="fa fa-eraser mr-1"></i>Cancel
              </QESButton>
            </> : null
          }
        </div>) : null
      }
    </div>
  );
}

export {
  AddFilterModel,
  FilterCriteria,
  FilterCriteriaModel,
  FilterCriteriaOnChangeResponse
}